import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'

import { Button } from 'reactstrap'
import { Link } from 'react-router-dom'

import ProgressBar, { ProgressBarStep } from 'src/common/ProgressBar.component'

import { RootState } from 'src/store/rootReducer'
import { JobApplicant } from 'src/lib/interfaces/JobApplicant.interface'
import { User, hasAnyPermission } from 'src/lib/interfaces/User.interface'
import {
  ApplicationStatus,
  orderedApplicationStatus,
  applicationStatusToString
} from 'src/lib/interfaces/ApplicationStatus.interface'

const map = require('lodash/map')
const size = require('lodash/size')
const indexOf = require('lodash/indexOf')
const includes = require('lodash/includes')

export interface ApplicationProgressBarProps {
  user: User
}

export interface ApplicationProgressBarPassedProps {
  application?: JobApplicant
  onDeny: (application: JobApplicant) => void
  onConvertToEmployee: (application: JobApplicant) => void
  onNextStep: (application: JobApplicant, nextStatus: ApplicationStatus) => void
}

interface ApplicationProgressBarStep {
  roles?: string[]
  subtitle?: string
  status: ApplicationStatus
  nextStepButtonTitle?: string
  progressBarStep: ProgressBarStep
}

class ApplicationProgressBar extends React.Component<ApplicationProgressBarProps & ApplicationProgressBarPassedProps> {
  constructor(props) {
    super(props)

    this.handleDeny = this.handleDeny.bind(this)
    this.handleNextStep = this.handleNextStep.bind(this)
    this.handleConvertToEmployee = this.handleConvertToEmployee.bind(this)
  }

  get currentStatusIndex() {
    const { application } = this.props

    if (!application || includes([ApplicationStatus.CLOSED, ApplicationStatus.DENIED], application.status)) {
      return undefined
    }

    return indexOf(orderedApplicationStatus, application.status)
  }

  get progressSteps(): ApplicationProgressBarStep[]|undefined {
    const { application } = this.props
    if (!application || !application.status) { return undefined }

    if (includes([ApplicationStatus.CLOSED, ApplicationStatus.DENIED], application.status)) {
      return [
        {
          status: application.status,
          nextStepButtonTitle: 'Reinstate',
          roles: ['ROLE_APPLICATION_ADMIN'],
          progressBarStep: { icon: ['far', 'times'], iconSize: 25, completed: false }
        }
      ]
    }

    if (application.status === ApplicationStatus.STARTED) {
      return [
        {
          status: application.status,
          nextStepButtonTitle: 'Open',
          roles: ['ROLE_APPLICATION_ADMIN'],
          progressBarStep: { icon: ['far', 'sticky-note'], iconSize: 25, completed: false }
        }
      ]
    }

    const statusIndex = this.currentStatusIndex
    return [
      // {
      //   status: orderedApplicationStatus[0],
      //   subtitle: 'Application was started',
      //   progressBarStep: {
      //     iconSize: 25,
      //     completed: statusIndex >= 0,
      //     icon: ['far', 'sticky-note']
      //   }
      // },
      {
        nextStepButtonTitle: 'Interview',
        status: orderedApplicationStatus[1],
        progressBarStep: {
          icon: ['fas', 'paper-plane'],
          completed: statusIndex >= 1
        }
      },
      {
        nextStepButtonTitle: 'In Review',
        status: orderedApplicationStatus[2],
        progressBarStep: {
          iconSize: 26,
          icon: ['far', 'comments'],
          completed: statusIndex >= 2
        }
      },
      {
        nextStepButtonTitle: 'Approve',
        roles: ['ROLE_APPLICATION_ADMIN'],
        status: orderedApplicationStatus[3],
        progressBarStep: {
          iconSize: 25,
          icon: ['far', 'building'],
          completed: statusIndex >= 3
        }
      },
      {
        roles: ['ROLE_APPLICATION_ADMIN'],
        status: orderedApplicationStatus[4],
        progressBarStep: {
          iconSize: 25,
          completed: statusIndex >= 4,
          icon: ['far', 'address-card']
        }
      }
    ]
  }

  handleDeny() {
    const { onDeny, application } = this.props
    onDeny(application!)
  }

  handleNextStep() {
    const { onNextStep, application } = this.props

    let nextStatus
    if (application!.status === ApplicationStatus.CLOSED || application!.status === ApplicationStatus.DENIED) {
      nextStatus = ApplicationStatus.COMPLETED
    }
    else {
      nextStatus = orderedApplicationStatus[this.currentStatusIndex + 1]
    }

    onNextStep(application!, nextStatus)
  }

  handleConvertToEmployee() {
    const { application, onConvertToEmployee } = this.props
    onConvertToEmployee(application!)
  }

  render() {
    const { user, application } = this.props

    const steps = this.progressSteps
    if (!steps || !steps.length) { return null }

    let stepWidth = 100 / steps.length
    if (steps.length === 1) {
      stepWidth = 25
    }

    return (
      <div className="w-100 d-flex flex-column">
        <ProgressBar steps={map(steps, 'progressBarStep')} />

        <div className="row no-gutters mt-3">
          {map(steps, (step, index) => {
            let hasNextStepPermission = true
            if (size(step.roles) > 0) {
              hasNextStepPermission = hasAnyPermission(user, step.roles)
            }

            return (
              <div
                key={index}
                className="pr-5"
                style={{ width: `${stepWidth}%`, maxWidth: `${stepWidth}%` }}
              >
                <div className="ggcard">
                  <div className="card-body">
                    <div className="card-subtitle">{applicationStatusToString(step.status)}</div>
                    {step.subtitle && <div className="mt-1">{step.subtitle}</div>}
                    {
                      application!.status !== ApplicationStatus.APPROVED &&
                      step.status === application!.status && (
                        <div className="d-flex align-items-center justify-content-between mt-2">
                          {application!.status !== ApplicationStatus.DENIED && (
                            <Button
                              size="sm"
                              color="dark"
                              className="ggbutton"
                              onClick={this.handleDeny}
                            >
                              Deny
                            </Button>
                          )}

                          {hasNextStepPermission && step.nextStepButtonTitle && (
                            <Button
                              size="sm"
                              color="primary"
                              className="ggbutton"
                              onClick={this.handleNextStep}
                            >
                              {step.nextStepButtonTitle}
                            </Button>
                          )}
                        </div>
                      )
                    }

                    {step.status === ApplicationStatus.APPROVED &&
                      application!.status === ApplicationStatus.APPROVED && (
                        <div
                          className={
                            classnames(
                              'd-flex align-items-center justify-content-between',
                              { 'mt-2': application!.employee || hasNextStepPermission }
                            )
                          }
                        >
                          {!application!.employee ? (
                              hasNextStepPermission && (
                                <Button
                                  size="sm"
                                  color="primary"
                                  className="ggbutton"
                                  onClick={this.handleConvertToEmployee}
                                >
                                  Convert to Employee
                                </Button>
                              )
                          ) : (
                            <Link to={`/employees/${application!.employee!.id}`}>
                              <u>Employee <b>{application!.employee!.id}</b></u>
                            </Link>
                          )}
                        </div>  
                      )
                    }
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user
})

export default connect<{}, {}, ApplicationProgressBarPassedProps>(mapStateToProps)(ApplicationProgressBar)



// WEBPACK FOOTER //
// ./src/routes/applications/components/ApplicationProgressBar.component.tsx